import {
  EmptySectionCard,
  GroupClassIllustrations,
  ChakraBox,
  ChakraFlex,
  SkeletonLoader,
  InfiniteScroll,
  GroupSectionListViewCard,
  HostInfo,
  TagColor,
  useToastMessage,
  FAIconName,
} from '@equip.health/ui';

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useGetMySectionsListQuery } from '~/store/services/groupClass';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

export interface SectionItem {
  id: string;
  groupId: string;
  title: string;
  iconName: GroupClassIllustrations;
  groupType: string;
  groupTypeTagColor: TagColor;
  frequency: SectionFrequencyCode;
  groupStatusCode: string;
  noOfSections: number;
  sensitive: boolean;
  schedule: string;
  timeSlot: string;
  hosts: HostInfo[];
  durationWeeks: string;
  startDate: string;
}

const TOP_LEVEL_ID = 'my-sections-list';

interface RenderSectionsListProps {
  groupClassTitle: string;
  totalCount: number;
}

const RenderSectionsList = ({
  groupClassTitle,
  totalCount,
}: RenderSectionsListProps) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sectionsList, setSectionsList] = useState<SectionItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const { createToast } = useToastMessage();
  const itemsFetchedSoFar = sectionsList.length;
  const hasMore = itemsFetchedSoFar < totalCount;

  const errorShown = useRef(false);

  const {
    data: sectionsResponse,
    isLoading: isLoadingSections,
    isError,
  } = useGetMySectionsListQuery({
    pageNumber,
    ...(groupClassTitle ? { title: groupClassTitle } : {}),
  });
  const isInitialLoad =
    isLoadingSections && pageNumber === 1 && sectionsList.length === 0;

  useLayoutEffect(() => {
    setSectionsList([]);
    setPageNumber(1);
    errorShown.current = false;
  }, [groupClassTitle]);

  useEffect(() => {
    if (sectionsResponse) {
      setSectionsList((prev) => [...prev, ...sectionsResponse]);
    }
    setIsFetching(false);
  }, [sectionsResponse]);

  // using this effect to show error toast only once when the error first occurs. else getting multiple toasts on every subsequent re-render while the error condition persists
  useEffect(() => {
    if (isError && !errorShown.current) {
      errorShown.current = true;
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: 'fetch-sections-error-toast',
        text: "We're sorry, we were unable to fetch sections. Please try again",
        variant: 'critical',
      });
    }
  }, [isError]);

  return (
    <ChakraBox height="100%" id={TOP_LEVEL_ID} overflow="auto">
      <ChakraFlex direction="column" marginRight="24px" paddingBottom="36px">
        <SkeletonLoader
          id={`${TOP_LEVEL_ID}__skeleton-loader`}
          isLoaded={!isInitialLoad}
        >
          {sectionsList.length > 0 ? (
            <InfiniteScroll
              dataLength={sectionsList.length}
              fetchData={() => {
                if (!isFetching && hasMore) {
                  setIsFetching(true);
                  setPageNumber((p) => p + 1);
                }
              }}
              hideCount
              id={`${TOP_LEVEL_ID}__infinite-scroll`}
              scrollableTarget={TOP_LEVEL_ID}
              showLoader={isFetching}
              totalCount={totalCount}
            >
              <ChakraFlex direction="column" gap={2}>
                {sectionsList.map((section) => (
                  <GroupSectionListViewCard
                    key={section.id}
                    {...section}
                    tooltipWrapper={({ children, providerExternalId }) => (
                      <ProviderAvatarTooltipWrapper
                        actionTime={section.startDate}
                        isRoleAtAction
                        placement="left"
                        providerExternalId={providerExternalId}
                      >
                        {children}
                      </ProviderAvatarTooltipWrapper>
                    )}
                  />
                ))}
              </ChakraFlex>
            </InfiniteScroll>
          ) : (
            !isFetching && (
              <EmptySectionCard
                caption={'No sections found'}
                id={`${TOP_LEVEL_ID}__empty`}
              />
            )
          )}
        </SkeletonLoader>
      </ChakraFlex>
    </ChakraBox>
  );
};

export default RenderSectionsList;
